import { parseCaipAccount } from "../kernel/types.js";
/**
 * Sign-In with Ethereum folded into the session proposal, so the wallet prompts once instead of
 * twice. This half only asks and binds. It never judges a signature: the browser is not a trust
 * boundary, so verification belongs to whoever consumes the CACAO. See `konekt/cacao`.
 */
const DID_PKH = "did:pkh:";
/**
 * Reads the CACAOs returned for this session's authentication request.
 *
 * @returns The response array, or an empty array when the session is absent or the wallet did not
 * answer authentication.
 */
export function cacaosOf(session) {
    const authentication = session?.proposalRequestsResponses?.authentication;
    return Array.isArray(authentication) ? authentication : [];
}
function accountOf(iss) {
    return iss.startsWith(DID_PKH) ? parseCaipAccount(iss.slice(DID_PKH.length)) : undefined;
}
function approvedAccounts(session) {
    const out = new Set();
    for (const namespace of Object.values(session.namespaces)) {
        for (const account of namespace.accounts ?? [])
            out.add(account.toLowerCase());
    }
    return out;
}
/**
 * Adds a CAIP-122 authentication request to the WalletConnect session proposal.
 *
 * The feature fetches a nonce in `onProposal`. After approval it checks that returned CACAOs match
 * that nonce, the requested domain and URI, and an account granted by the session. It does not
 * verify signatures.
 *
 * Send the returned CACAOs to the server that makes the authentication decision. That server must
 * call both `verifyCacao()` and `checkClaims()` from `konekt/cacao`.
 *
 * @example
 * ```ts
 * features: [
 *   siwe({
 *     domain: location.host,
 *     uri: location.origin,
 *     chains: ["eip155:1"],
 *     getNonce: () => fetch("/auth/nonce").then((response) => response.text()),
 *   }),
 * ]
 * ```
 *
 * @throws When `resources` contains an unsupported `urn:recap:` entry.
 */
export function siwe(options) {
    if (options.resources?.some((r) => r.startsWith("urn:recap:"))) {
        throw new Error("siwe does not support recap resources: the statement rewrite they require is not implemented.");
    }
    // Carried from the proposal to the settle so the echo can be checked. One instance, one provider.
    let issuedNonce;
    return {
        name: "siwe",
        async onProposal(proposal) {
            const nonce = await options.getNonce();
            issuedNonce = nonce;
            const request = {
                domain: options.domain,
                chains: options.chains,
                nonce,
                type: "caip122",
                aud: options.uri,
                version: "1",
                iat: new Date().toISOString(),
                statement: options.statement,
                exp: options.exp,
                nbf: options.nbf,
                requestId: options.requestId,
                resources: options.resources,
            };
            return { ...proposal, requests: { ...proposal.requests, authentication: [request] } };
        },
        onSettle(session) {
            const cacaos = cacaosOf(session);
            if (!cacaos.length) {
                if (options.required === false)
                    return;
                throw new Error("The wallet settled a session without answering the authentication request. " +
                    "Pass required: false to allow an unauthenticated session.");
            }
            const accounts = approvedAccounts(session);
            for (const { p } of cacaos) {
                if (p.nonce !== issuedNonce)
                    throw new Error("A returned CACAO carries a nonce this app never issued.");
                if (p.domain !== options.domain) {
                    throw new Error(`A returned CACAO is for domain "${p.domain}", not "${options.domain}".`);
                }
                const target = p.aud ?? p.uri;
                if (target !== options.uri)
                    throw new Error(`A returned CACAO is for URI "${target}", not "${options.uri}".`);
                const account = accountOf(p.iss);
                if (!account)
                    throw new Error(`A returned CACAO has a malformed issuer "${p.iss}".`);
                // Signed by an account the session never granted: authenticated as one address, transacting as another.
                if (!accounts.has(`${account.chainId}:${account.address}`.toLowerCase())) {
                    throw new Error(`A returned CACAO is signed by ${p.iss}, which the session does not grant.`);
                }
            }
        },
        onDisconnect() {
            issuedNonce = undefined;
        },
    };
}
//# sourceMappingURL=siwe.js.map